import React, { useEffect, useMemo, useState } from 'react'
import { TextField, MenuItem, Button, Autocomplete, Drawer, IconButton, Checkbox, FormControlLabel } from '@mui/material'
import { toast } from 'react-toastify'
import { Modal } from 'antd'
import {
  getPromos, registrarPromo, actualizarPromo, marcarSinBono, retirarPromo, getMaterialesCatalogo,
  getCiudades, subirImagenBono, getBonoEstrategias, getBonoMateriales, setBonoMateriales, duplicarBono, publicarBono,
} from 'src/services/preciosService'
import TablaPrecios from './TablaPrecios'
import CoberturaPorMoto from './CoberturaPorMoto'
import { vehiculosDe, esNacional, pendientesDe, pendientesLuciaDe } from './bonosUtils'
import VehiculosDelBono from './VehiculosDelBono'
import PreviewBono from './PreviewBono'
import MoneyField from './MoneyField'
import { fmt, colAcciones, chip } from './columnasPrecios'

// Ayuda de los campos en una sola línea: así los campos de una misma fila del paso 2 quedan de la misma altura.
const ayudaUnaLinea = { sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }

// Máximo de caracteres de los textos del bono. La etiqueta va a VTEX (31); el resto es el tamaño de su
// columna (el detalle también se guarda en precios_reglas.valor_texto, de 200).
const LIMITES = { nombre_promo: 200, beneficio: 200, etiqueta: 31, financiera: 60 }
const largo = (v) => String(v || '').length
const cuenta = (v, max) => `${largo(v)}/${max}`

const vacio = {
  // La financiera se pide solo en las estrategias de financiación: un bono comercial no la lleva.
  material: '', modelo_texto: '', nombre_promo: '', tipo: 'progreser', financiera: '',
  monto: '', auteco: '', aporte_financiera: '', estrategia: '',
  beneficio: '', etiqueta: '', desde: '', hasta: '', legal: '', img_url: '',
  // F105: horas opcionales ('' = todo el día)
  hora_desde: '', hora_hasta: '',
}

const plantillaLegal = (f, cobertura) =>
  `Vigencia: por desembolso, del ${f.desde || '—'} al ${f.hasta || '—'}. Cobertura: ${cobertura}. Aplica en concesionarios propios y distribuidores Auteco con convenio activo ProgreSER. Sujeto a estudio de crédito. No acumulable, no transferible, no canjeable, no reembolsable en efectivo.`


const num = (v) => Number(String(v || '').replace(/\D/g, '')) || 0

/** Paso numerado, mismo patrón que el armado del plano: se entiende sin manual. */
const Paso = ({ n, titulo, ayuda, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
      <span
        style={{
          background: '#0f2439',
          color: '#fff',
          width: 22,
          height: 22,
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          flex: '0 0 auto',
        }}
      >
        {n}
      </span>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0f2439' }}>{titulo}</div>
        {ayuda && <div style={{ fontSize: 12, color: '#6b7684', marginTop: 2 }}>{ayuda}</div>}
      </div>
    </div>
    <div style={{ paddingLeft: 32 }}>{children}</div>
  </div>
)

const PromosTab = ({ onChanged, readOnly = false }) => {
  const [promos, setPromos] = useState([])
  const [materiales, setMateriales] = useState([])
  const [zonas, setZonas] = useState([])
  // F59: las estrategias del documento de Comercial/Financiacion.
  const [estrategias, setEstrategias] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [f, setF] = useState(vacio)
  const [selMats, setSelMats] = useState([])
  // Se elige por MOTO, no por color. El selector listaba los ~3.074 materiales con un renderOption
  // propio (un punto de color por fila) y MUI no virtualiza eso: la lista se trababa. Además el bono
  // se decide por modelo, igual que el precio — los colores van juntos, como en Trade.
  const [selMotos, setSelMotos] = useState([])
  const [editId, setEditId] = useState(null) // id del bono abierto (ver/editar); null al crear
  // El panel del bono: null = cerrado; 'crear' | 'ver' | 'editar'. Los tres usan el MISMO formulario;
  // 'ver' lo deja en solo lectura.
  const [modo, setModo] = useState(null)
  const soloVer = modo === 'ver'
  // F72: cobertura y términos por moto. `cobServer` es lo guardado (por material); `cobEdits` lo tocado.
  const [cobServer, setCobServer] = useState({})
  const [cobEdits, setCobEdits] = useState({})
  const [estadoBono, setEstadoBono] = useState('')
  // F98: motos del bono que aún no están en el catálogo de Lucia ([{ material, vehiculo }]) y el
  // formulario corto para agregarlas.
  const [pendLucia, setPendLucia] = useState([])
  const [pendCod, setPendCod] = useState('')
  const [pendVeh, setPendVeh] = useState('')
  // Filtro de la tabla por estrategia: null = todas; '' = sin estrategia.
  const [filtroEst, setFiltroEst] = useState(null)
  // Filtro por estado: null = todos; 'vigente' | 'vencido'.
  const [filtroEstado, setFiltroEstado] = useState(null)
  // La tabla por bono (una fila por bono) o por material (una fila por moto de cada bono).
  const [vistaPor, setVistaPor] = useState('bono')
  // Paso 4: el mismo texto legal para todos los vehículos del bono.
  const [legalComun, setLegalComun] = useState(false)
  // F103: el bono abierto es un borrador (no sale en la API hasta publicarlo).
  const [esBorrador, setEsBorrador] = useState(false)

  const fetchData = async () => {
    setIsLoading(true)
    // F101: el selector usa todo el catálogo activo (también las motos que Trade aún no pricea).
    // F104: la cobertura de los bonos es por ciudad (no por zonas comerciales).
    const [pr, mats, zn] = await Promise.all([getPromos(), getMaterialesCatalogo(), getCiudades()])
    if (pr && pr.success) setPromos(pr.data)
    if (mats && mats.success) setMateriales(mats.data)
    // F106: departamentos y ciudades ({ etiqueta, tipo }). Si el back aún no tiene f104/f106 (sin reiniciar),
    // devuelve las zonas: se usan igual mientras tanto, como ciudades.
    if (zn && zn.success) {
      setZonas(zn.data
        .map((r) => ({ etiqueta: r.etiqueta || r.zona, tipo: r.tipo || 'ciudad' }))
        .filter((r) => r.etiqueta))
    }
    setIsLoading(false)
  }

  useEffect(() => {
    fetchData()
    getBonoEstrategias().then((r) => setEstrategias((r && r.data) || []))
  }, [])

  /** Una opción por MOTO (modelo + año), con sus colores adentro. ~1.100 en vez de ~3.074. */
  const motosOpt = useMemo(() => {
    const m = new Map()
    materiales.forEach((x) => {
      const clave = `${x.marca || ''}|${x.modelo}|${x.anio_modelo || ''}`
      if (!m.has(clave)) {
        m.set(clave, {
          clave,
          marca: x.marca,
          modelo: x.modelo,
          anio_modelo: x.anio_modelo,
          mats: [],
          etiqueta: `${x.marca || ''} ${x.modelo} ${x.anio_modelo || ''}`.trim(),
        })
      }
      m.get(clave).mats.push(x)
    })
    // F101: el catálogo trae también motos que Trade aún no pricea (con_precio = 0): se marcan.
    return [...m.values()]
      .map((o) => ({ ...o, sinPrecio: o.mats.every((x) => String(x.con_precio) === '0') }))
      .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta))
  }, [materiales])

  /** El selector busca por marca, modelo, año, color o código de material; máximo 200 para que no se trabe. */
  const filtrarMotos = (opts, { inputValue }) => {
    const q = String(inputValue || '').trim().toLowerCase()
    const lista = !q ? opts : opts.filter((o) => o.etiqueta.toLowerCase().includes(q)
      || o.mats.some((x) => String(x.material).includes(q) || String(x.color || '').toLowerCase().includes(q)))
    return lista.slice(0, 200)
  }

  /**
   * Al agregar un vehículo entran TODOS sus colores; los vehículos que ya estaban conservan los colores
   * que se habían quitado (antes se perdían al tocar el selector). Al sacar un vehículo salen sus colores.
   */
  const elegirMotos = (v) => {
    const antes = new Set(selMotos.map((x) => x.clave))
    const ahora = new Set(v.map((x) => x.clave))
    const nuevos = v.filter((x) => !antes.has(x.clave)).flatMap((x) => x.mats)
    const salen = new Set(selMotos.filter((x) => !ahora.has(x.clave)).flatMap((x) => x.mats.map((m) => m.material)))
    setSelMotos(v)
    setSelMats((prev) => {
      const quedan = prev.filter((m) => !salen.has(m.material))
      const ya = new Set(quedan.map((m) => m.material))
      return [...quedan, ...nuevos.filter((m) => !ya.has(m.material))]
    })
  }

  /**
   * El «modelo o referencia» ya no se teclea: son los vehículos elegidos (marca + modelo), igual que lo
   * arma la sincronización de TP. El texto principal del bono es el Nombre de la promoción.
   */
  const vehiculosTexto = useMemo(() => {
    const vistos = []
    selMats.forEach((m) => {
      const t = `${String(m.marca || '').toUpperCase()} ${m.modelo || ''}`.trim()
      if (t && !vistos.includes(t)) vistos.push(t)
    })
    return vistos.join(' / ').slice(0, 200)
  }, [selMats])
  // F98: si el bono solo tiene motos que aún no están en Lucia, el texto sale de sus nombres.
  const modeloAGuardar = () => vehiculosTexto
    || [...new Set(pendLucia.map((p) => p.vehiculo))].join(' / ').slice(0, 200)
    || f.modelo_texto

  /** Prende o apaga un color del bono. */
  const alternarColor = (m) => setSelMats((prev) => (prev.some((x) => x.material === m.material)
    ? prev.filter((x) => x.material !== m.material)
    : [...prev, m]))

  const quitarVehiculo = (clave) => elegirMotos(selMotos.filter((x) => x.clave !== clave))

  /** Materiales del bono que no están en ningún vehículo del catálogo con cambio (bonos viejos). */
  const sueltos = useMemo(() => {
    const deVehiculos = new Set(selMotos.flatMap((x) => x.mats.map((m) => m.material)))
    return selMats.filter((m) => !deVehiculos.has(m.material))
  }, [selMotos, selMats])

  const incluidos = useMemo(() => new Set(selMats.map((m) => m.material)), [selMats])

  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }))
  const setNum = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }))

  /** Sube una imagen y devuelve su URL pública ('' si falla). La pieza es por moto (F73). */
  const subirPieza = (file) => new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = async () => {
      const res = await subirImagenBono(reader.result)
      resolve(res && res.success && res.url ? res.url : '')
    }
    reader.onerror = () => resolve('')
    reader.readAsDataURL(file)
  })

  // 'Nacional' + las ciudades (F104): lo que se puede elegir como cobertura de cada vehículo (paso 4).
  // F106: primero los departamentos y luego las ciudades (el SP ya las trae en ese orden).
  const coberturaOpts = useMemo(() => ['Nacional', ...zonas.map((z) => z.etiqueta)], [zonas])
  const grupoCobertura = useMemo(() => {
    const m = new Map(zonas.map((z) => [z.etiqueta, z.tipo === 'departamento' ? 'Departamentos' : 'Ciudades']))
    return (o) => (o === 'Nacional' ? 'Nacional' : (m.get(o) || 'Ciudades'))
  }, [zonas])

  const estrategiaSel = useMemo(
    () => estrategias.find((e) => e.clave === f.estrategia) || null,
    [estrategias, f.estrategia],
  )

  // Para el resumen y la tabla: el efecto en dos o tres palabras.
  const EFECTO_CORTO = { tacha: 'Tacha el precio', precio_financiera: 'Precio con la financiera', beneficio: 'Solo tag de beneficio' }

  const EFECTO_TXT = {
    tacha: 'Se TACHA el precio en el sitio: se muestra el anterior tachado y el nuevo.',
    precio_financiera: 'NO se tacha. Se muestra el precio que queda con esa financiera, en más beneficios.',
    beneficio: 'NO se toca el precio. Va como tag en el slider de más beneficios.',
  }

  /**
   * ¿La estrategia lleva plata o va en especie? Es lo que decide qué campo pedir. El back valida lo
   * mismo (`sp_precios_bono_registrar_v2`): con lleva_monto=1 exige monto; con 0 exige decir qué se
   * entrega y descarta el monto. Sin estrategia elegida todavía, se asume que lleva monto.
   */
  const llevaMonto = !estrategiaSel
    || estrategiaSel.lleva_monto === 1
    || estrategiaSel.lleva_monto === '1'
    || estrategiaSel.lleva_monto === true

  /**
   * Orden de la tabla: primero los que TACHAN el precio, después por plata de mayor a menor (los en
   * especie, sin monto, al final de su grupo). La tabla deja reordenar por columna igual.
   */
  /** F88: las motos que esperan catálogo del bono abierto en el panel (ver / editar). */
  const pendientesBono = useMemo(
    () => (editId ? pendientesDe(promos.find((p) => Number(p.id) === Number(editId))) : []),
    [editId, promos],
  )

  const promosOrdenados = useMemo(() => [...promos].sort((a, b) => {
    const ta = a.efecto === 'tacha' ? 0 : 1
    const tb = b.efecto === 'tacha' ? 0 : 1
    if (ta !== tb) return ta - tb
    return Number(b.monto || 0) - Number(a.monto || 0)
  }), [promos])

  // Botones del filtro: las estrategias que tienen bonos, con cuántos tiene cada una (más usadas primero).
  const opcionesEst = useMemo(() => {
    const m = new Map()
    promos.forEach((p) => {
      const k = p.estrategia || ''
      if (!m.has(k)) m.set(k, { clave: k, nombre: p.estrategia_nombre || 'Sin estrategia', n: 0 })
      m.get(k).n += 1
    })
    return [...m.values()].sort((a, b) => b.n - a.n)
  }, [promos])
  // F103: un borrador no es vigente ni vencido: es borrador.
  const estadoDe = (p) => (String(p.borrador) === '1' ? 'borrador' : p.estado)
  const promosFiltrados = useMemo(
    () => promosOrdenados
      .filter((p) => filtroEst === null || (p.estrategia || '') === filtroEst)
      .filter((p) => filtroEstado === null || estadoDe(p) === filtroEstado),
    [promosOrdenados, filtroEst, filtroEstado],
  )
  const nEstado = (e) => promos.filter((p) => estadoDe(p) === e).length

  /**
   * «Ver por material»: una fila por moto (material) de cada bono filtrado, con su bono al lado. Incluye las
   * motos que esperan catálogo (de Lucia y de TP). El catálogo da marca, modelo, año y color.
   */
  const matPorCodigo = useMemo(() => new Map(materiales.map((m) => [String(m.material), m])), [materiales])
  const filasMaterial = useMemo(() => {
    const filas = []
    promosFiltrados.forEach((p) => {
      const base = { bono: p, id_bono: p.id }
      String(p.materiales_csv || '').split(',').map((c) => c.trim()).filter(Boolean).forEach((cod) => {
        const m = matPorCodigo.get(cod) || {}
        filas.push({
          ...base, key: `${p.id}-${cod}`, material: cod, color: m.color || '',
          vehiculo: `${m.marca || ''} ${m.modelo || ''} ${m.anio_modelo || ''}`.trim() || '(no está en el catálogo)',
          espera: '',
        })
      })
      pendientesLuciaDe(p).forEach((x) => filas.push({
        ...base, key: `${p.id}-L${x.material}`, material: x.material, color: '', vehiculo: x.vehiculo, espera: 'Lucia',
      }))
      pendientesDe(p).forEach((v) => v.colores.forEach((c) => filas.push({
        ...base, key: `${p.id}-T${c.material || c.sku}`, material: c.material || `SKU ${c.sku}`, color: c.color || '',
        vehiculo: v.vehiculo, espera: 'TP',
      })))
    })
    return filas
  }, [promosFiltrados, matPorCodigo])

  /** F98: agrega una moto que aún no está en el catálogo (código de material + nombre del vehículo). */
  const agregarPendiente = () => {
    const cod = pendCod.trim()
    const veh = pendVeh.trim().toUpperCase()
    if (!/^\d{6,12}$/.test(cod)) {
      toast.error('El código de material son solo números (ej. 60006652).')
      return
    }
    if (!veh) {
      toast.error('Escribe el vehículo (marca, modelo y año) para reconocerla.')
      return
    }
    if (pendLucia.some((p) => p.material === cod) || selMats.some((m) => m.material === cod)) {
      toast.error(`La moto ${cod} ya está en este bono.`)
      return
    }
    if (materiales.some((m) => m.material === cod)) {
      toast.error(`La moto ${cod} ya está en Lucia: agrégala desde «Agregar vehículo».`)
      return
    }
    // F99: arranca Nacional, igual que un vehículo nuevo del catálogo; el resto se pone en el paso 4. Si ya
    // hay otro código de ese mismo vehículo, hereda lo que ese tenga (es la misma tarjeta).
    const base = pendLucia.find((p) => String(p.vehiculo || '').trim().toUpperCase() === veh) || {}
    setPendLucia((prev) => [...prev, {
      material: cod,
      vehiculo: veh.slice(0, 200),
      cobertura: base.cobertura || 'Nacional',
      legal: base.legal || '',
      img_url: base.img_url || '',
      precio_antes: base.precio_antes || '',
    }])
    setPendCod('')
    setPendVeh('')
  }

  /** Financiación (o Auteco + financiación): el bono es de UNA financiera y hay que decir cuál. */
  const esDeFinanciera = (est) => Boolean(est && (est.area === 'financiacion' || est.area === 'ambas'))
  const pideFinanciera = esDeFinanciera(estrategiaSel)
  // Precio estructural: a SAP va el precio nuevo; Lucia guarda el de antes por vehículo para tachar.
  const esEstructural = Boolean(estrategiaSel && estrategiaSel.clave === 'PRECIO_ESTRUCTURAL')

  /**
   * Las financieras que ya se usan en bonos, para elegir sin teclear. «Otros medios de pago» no es una
   * financiera y el viejo «FINANCIANDO CON …» es el texto del nombre, no la financiera.
   */
  const financierasConocidas = useMemo(() => {
    const s = new Set(['PROGRESER'])
    promos.forEach((p) => {
      String(p.financiera || '').split(',').map((x) => x.trim().toUpperCase()).forEach((x) => {
        if (x && !x.includes('MEDIOS') && !x.startsWith('FINANCIANDO')) s.add(x)
      })
    })
    return [...s].sort()
  }, [promos])

  /** Cambiar de estrategia limpia el campo que deja de aplicar, para no mandar datos que el back tira. */
  const cambiarEstrategia = (e) => {
    const clave = e.target.value
    const est = estrategias.find((x) => x.clave === clave) || null
    const lleva = !est || est.lleva_monto === 1 || est.lleva_monto === '1' || est.lleva_monto === true
    setF((prev) => {
      // Al alta: financiación sugiere PROGRESER; las demás estrategias van sin financiera. Al editar se
      // respeta la que traía (ej. un comercial de TP con «OTROS MEDIOS DE PAGO»).
      let { financiera } = prev
      if (esDeFinanciera(est)) financiera = financiera || 'PROGRESER'
      else if (!editId) financiera = ''
      return {
        ...prev,
        estrategia: clave,
        financiera,
        // F92: el detalle de beneficio vale para toda estrategia; solo se limpia la plata en especie.
        ...(lleva ? {} : { monto: '', auteco: '', aporte_financiera: '' }),
      }
    })
  }

  /**
   * Solo «Bono Auteco + financiación» (área 'ambas') reparte de verdad entre dos que ponen. En las
   * demás el reparto sale de la estrategia y no se pide: comercial = todo Auteco, financiación = todo
   * la financiera. Antes se tecleaba tres veces el mismo número.
   */
  const usaSplit = Boolean(estrategiaSel && estrategiaSel.area === 'ambas')

  // En «Auteco + financiación» no se teclea el total: es lo que pone Auteco más lo que pone la
  // financiera. En las demás, el monto es el único campo.
  const totalSplit = num(f.auteco) + num(f.aporte_financiera)
  const montoNum = usaSplit ? totalSplit : num(f.monto)
  const montoAGuardar = () => (usaSplit ? String(totalSplit) : f.monto)

  /** Lo que se guarda en auteco / aporte_financiera: el reparto tecleado o el que dicta la estrategia. */
  const repartoAGuardar = () => {
    if (usaSplit || !estrategiaSel) return { auteco: f.auteco, aporte_financiera: f.aporte_financiera }
    if (!llevaMonto) return { auteco: '', aporte_financiera: '' }
    return estrategiaSel.area === 'financiacion'
      ? { auteco: '0', aporte_financiera: f.monto }
      : { auteco: f.monto, aporte_financiera: '0' }
  }

  /** Cierra el panel y deja el formulario limpio. */
  const cerrarPanel = () => {
    setModo(null)
    setEditId(null)
    setF(vacio)
    setSelMats([])
    setSelMotos([])
    setPendLucia([])
    setPendCod('')
    setPendVeh('')
    setLegalComun(false)
    setEsBorrador(false)
    setCobServer({})
    setCobEdits({})
    setEstadoBono('')
  }

  const abrirNuevo = () => {
    cerrarPanel()
    setModo('crear')
  }

  // ---- F72: cobertura y términos por moto, dentro del mismo formulario ----
  const cobValor = (material, campo) => {
    const e = cobEdits[material]
    if (e && e[campo] !== undefined) return e[campo]
    return (cobServer[material] && cobServer[material][campo]) || ''
  }

  /** Una fila por moto elegida, con lo guardado o lo tecleado. */
  const filasCob = useMemo(() => selMats.map((m) => {
    const s = cobServer[m.material] || {}
    const marca = m.marca || s.marca || ''
    const modelo = m.modelo || s.modelo || ''
    const anio = m.anio_modelo || s.anio_modelo || ''
    // La cobertura es del vehículo y no se hereda: si la moto no tiene propia se muestra la que
    // regía (la del bono, en bonos viejos) o 'Nacional' para una nueva, y al guardar queda escrita.
    const coberturaBase = String(s.cobertura || s.cobertura_bono || 'Nacional').trim()
    const e = cobEdits[m.material]
    const cobertura = e && e.cobertura !== undefined ? e.cobertura : coberturaBase
    const legal = cobValor(m.material, 'legal')
    const imgUrl = cobValor(m.material, 'img_url')
    // F84: precio antes (precio estructural). La base lo manda como número o null.
    const antesServer = s.precio_antes != null && Number(s.precio_antes) > 0 ? String(s.precio_antes) : ''
    const precioAntes = e && e.precio_antes !== undefined ? String(e.precio_antes || '') : antesServer
    return {
      material: m.material,
      etiqueta: `${marca} ${modelo} ${anio}`.trim(),
      vehiculo: `${marca} ${modelo}`.trim(),
      anio: Number(anio) || 0,
      color: m.color || s.color || '',
      cobertura,
      legal,
      img_url: imgUrl,
      precio_antes: precioAntes,
      pvp_actual: Number(s.pvp_actual || m.pvp_actual || m.pvp || 0),
      pvp_sap: Number(s.pvp_sap || m.pvp_sap || 0),
      cambiada: String(cobertura).trim() !== coberturaBase
        || String(legal).trim() !== String(s.legal || '').trim()
        || String(imgUrl).trim() !== String(s.img_url || '').trim()
        || num(precioAntes) !== num(antesServer),
      // Sin cobertura propia guardada: se escribe al guardar aunque no se haya tocado.
      sinCoberturaPropia: !String(s.cobertura || '').trim(),
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [selMats, cobServer, cobEdits])

  /**
   * El legal, la pieza y la cobertura son del VEHÍCULO (marca + modelo + año), no del color: un
   * vehículo con varios colores lleva uno solo de cada uno. Se agrupan las filas por vehículo; al
   * editar, el valor se copia a todos sus colores (la base guarda por material, así la API por
   * material/SKU no cambia). Si los colores ya traían valores distintos, se marca `mixto`.
   */
  const gruposCob = useMemo(() => {
    const mapa = new Map()
    selMats.forEach((m) => {
      const s = cobServer[m.material] || {}
      const marca = m.marca || s.marca || ''
      const modelo = m.modelo || s.modelo || ''
      const anio = m.anio_modelo || s.anio_modelo || ''
      const clave = `${marca}|${modelo}|${anio}`
      if (!mapa.has(clave)) mapa.set(clave, { clave, etiqueta: `${marca} ${modelo} ${anio}`.trim(), mats: [] })
      mapa.get(clave).mats.push(m.material)
    })
    const porMaterial = new Map(filasCob.map((x) => [x.material, x]))
    // Mismo orden que las tarjetas del paso 1 (orden en que se eligieron los vehículos).
    const pos = new Map(selMotos.map((x, i) => [x.clave, i]))
    const ordenados = [...mapa.values()].sort((a, b) => (pos.has(a.clave) ? pos.get(a.clave) : 999) - (pos.has(b.clave) ? pos.get(b.clave) : 999))
    return ordenados.map((g) => {
      const filas = g.mats.map((mat) => porMaterial.get(mat)).filter(Boolean)
      const unicos = (campo) => [...new Set(filas.map((x) => String(x[campo] || '').trim()))]
      const valor = (campo) => {
        const u = unicos(campo)
        return u.length === 1 ? u[0] : ''
      }
      return {
        clave: g.clave,
        material: g.clave, // CoberturaPorMoto identifica la fila con `material`
        etiqueta: g.etiqueta || g.mats[0],
        colores: filas.map((x) => ({ material: x.material, color: x.color })),
        cobertura: valor('cobertura'),
        legal: valor('legal'),
        img_url: valor('img_url'),
        precio_antes: valor('precio_antes'),
        pvp_actual: Math.max(0, ...filas.map((x) => Number(x.pvp_actual || 0))),
        pvp_sap: Math.max(0, ...filas.map((x) => Number(x.pvp_sap || 0))),
        mixto: {
          cobertura: unicos('cobertura').length > 1 ? unicos('cobertura') : null,
          legal: unicos('legal').length > 1,
          img_url: unicos('img_url').length > 1,
          precio_antes: unicos('precio_antes').length > 1,
        },
        cambiada: filas.some((x) => x.cambiada),
        mats: g.mats,
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selMats, selMotos, cobServer, filasCob])

  /**
   * F99: las motos que aún no están en Lucia también se configuran en el paso 4 (cobertura, legal, pieza
   * y precio antes). Van como un vehículo más; su clave lleva 'pend:' y sus cambios viven en pendLucia.
   */
  // Varios códigos con el mismo nombre de vehículo son UNA tarjeta (como los colores de una moto).
  const gruposPend = useMemo(() => {
    const mapa = new Map()
    pendLucia.forEach((p) => {
      const veh = String(p.vehiculo || '').trim().toUpperCase()
      if (!mapa.has(veh)) mapa.set(veh, [])
      mapa.get(veh).push(p)
    })
    return [...mapa.entries()].map(([veh, ps]) => {
      const unicos = (campo) => [...new Set(ps.map((x) => String(x[campo] || '').trim()))]
      const valor = (campo, def = '') => (unicos(campo).length === 1 ? unicos(campo)[0] || def : def)
      return {
        clave: `pend:${veh}`,
        material: `pend:${veh}`,
        etiqueta: `${veh} · esperando catálogo`,
        colores: ps.map((x) => ({ material: x.material, color: '' })),
        cobertura: valor('cobertura', 'Nacional'),
        legal: valor('legal'),
        img_url: valor('img_url'),
        precio_antes: valor('precio_antes'),
        pvp_actual: 0,
        pvp_sap: 0,
        mixto: {
          cobertura: unicos('cobertura').length > 1 ? unicos('cobertura') : null,
          legal: unicos('legal').length > 1,
          img_url: unicos('img_url').length > 1,
          precio_antes: unicos('precio_antes').length > 1,
        },
        cambiada: false,
        mats: [],
      }
    })
  }, [pendLucia])
  /**
   * F107: las motos de TP que esperan catálogo también se configuran en el paso 4. Mientras no se toquen
   * mandan los datos de TP; al cambiar algo pasan a ser pendientes de Lucia de este bono (gruposPend).
   */
  const gruposTp = useMemo(() => {
    const enLucia = new Set(pendLucia.map((p) => p.material))
    return pendientesBono
      .map((p) => ({ ...p, colores: p.colores.filter((c) => c.material && !enLucia.has(c.material)) }))
      .filter((p) => p.colores.length)
      .map((p) => ({
        clave: `tp:${String(p.vehiculo).trim().toUpperCase()}`,
        material: `tp:${String(p.vehiculo).trim().toUpperCase()}`,
        etiqueta: `${p.vehiculo} · de TP, esperando catálogo`,
        colores: p.colores.map((c) => ({ material: c.material, color: c.color })),
        cobertura: '', legal: '', img_url: '', precio_antes: '',
        pvp_actual: 0, pvp_sap: 0, mixto: {}, cambiada: false, mats: [],
      }))
  }, [pendientesBono, pendLucia])

  const vehiculosCob = useMemo(() => [...gruposCob, ...gruposPend, ...gruposTp], [gruposCob, gruposPend, gruposTp])

  /** F107: pasa las motos de un vehículo de TP a pendientes de Lucia con el cambio pedido (y Nacional por defecto). */
  const tomarTp = (g, cambios) => {
    const veh = String(g.clave).slice(3)
    setPendLucia((prev) => {
      const ya = new Set(prev.map((p) => p.material))
      return [...prev, ...g.colores.filter((c) => !ya.has(c.material)).map((c) => ({
        material: c.material, vehiculo: veh, cobertura: 'Nacional', legal: '', img_url: '', precio_antes: '', ...cambios,
      }))]
    })
  }

  /** Paso 1: las motos que esperan catálogo, en tarjetas por vehículo (primero las de Lucia, luego las de TP). */
  const pendientesTarjetas = useMemo(() => [
    ...gruposPend.map((g) => ({ clave: g.clave, etiqueta: g.clave.slice(5), origen: 'lucia', mats: g.colores })),
    // Las de TP que ya se configuraron en Lucia salen arriba como de Lucia, no repetidas.
    ...gruposTp.map((g) => ({ clave: g.clave, etiqueta: g.clave.slice(3), origen: 'tp', mats: g.colores })),
  ], [gruposPend, gruposTp])

  /** Cambiar un campo del vehículo = cambiarlo en todos sus colores. */
  const cambiarCobVehiculo = (clave, campo, valor) => {
    const g = gruposCob.find((x) => x.clave === clave)
    if (!g) return
    setCobEdits((prev) => {
      const sig = { ...prev }
      g.mats.forEach((mat) => { sig[mat] = { ...(sig[mat] || {}), [campo]: valor } })
      return sig
    })
  }

  /** El mismo texto legal en TODOS los vehículos del bono (catálogo y los que esperan catálogo). */
  const legalATodos = (valor) => {
    setCobEdits((prev) => {
      const sig = { ...prev }
      gruposCob.forEach((g) => g.mats.forEach((mat) => { sig[mat] = { ...(sig[mat] || {}), legal: valor } }))
      return sig
    })
    setPendLucia((prev) => prev.map((p) => ({ ...p, legal: valor })))
    // F107: también las motos de TP que esperan catálogo (pasan a Lucia con ese legal).
    gruposTp.forEach((g) => tomarTp(g, { legal: valor }))
  }

  // ¿Todos los vehículos tienen hoy el mismo legal (y no vacío)? Se avisa junto al check.
  const legalesIguales = vehiculosCob.length > 1
    && String(vehiculosCob[0].legal || '').trim() !== ''
    && vehiculosCob.every((v) => String(v.legal || '').trim() === String(vehiculosCob[0].legal || '').trim())

  /** Al marcar el check, el legal del primer vehículo que tenga uno pasa a todos. */
  const alternarLegalComun = (on) => {
    setLegalComun(on)
    if (!on) return
    const fuente = vehiculosCob.find((v) => String(v.legal || '').trim())
    if (!fuente) return
    const distintos = vehiculosCob.some((v) => String(v.legal || '').trim() !== String(fuente.legal).trim())
    legalATodos(fuente.legal)
    if (distintos) toast.info(`Se copió a todos los vehículos el legal de ${fuente.etiqueta}. Falta guardar el bono.`)
  }

  /** Paso 4: un vehículo del catálogo cambia sus colores; una moto pendiente (F99), su fila en pendLucia. */
  const cambiarCob = (clave, campo, valor) => {
    // Con «mismo texto legal para todos», el legal que se escribe en un vehículo va a todos.
    if (campo === 'legal' && legalComun && vehiculosCob.length > 1) {
      legalATodos(valor)
      return
    }
    if (String(clave).startsWith('tp:')) {
      const g = gruposTp.find((x) => x.clave === clave)
      if (g) tomarTp(g, { [campo]: valor })
      return
    }
    if (String(clave).startsWith('pend:')) {
      // Todos los códigos de ese vehículo pendiente.
      const veh = String(clave).slice(5)
      setPendLucia((prev) => prev.map((p) => (String(p.vehiculo || '').trim().toUpperCase() === veh ? { ...p, [campo]: valor } : p)))
      return
    }
    cambiarCobVehiculo(clave, campo, valor)
  }

  const subirPiezaVehiculo = async (clave, file) => {
    const url = await subirPieza(file)
    if (!url) {
      toast.error('No se pudo subir la imagen.')
      return
    }
    cambiarCob(clave, 'img_url', url)
    const g = vehiculosCob.find((x) => x.clave === clave)
    toast.info(`Pieza cargada para ${g ? g.etiqueta : 'el vehículo'}. Falta guardar el bono.`)
  }

  /**
   * Cobertura del bono para lo que todavía es de bono (efecto de la estrategia, cobertura_tipo, texto
   * de respaldo): nacional si TODOS sus vehículos son 'Nacional'; si alguno tiene zonas, regional.
   */
  const coberturaTipo = () => (vehiculosCob.length && vehiculosCob.every((g) => esNacional(g.cobertura || 'Nacional'))
    ? 'nacional'
    : (vehiculosCob.length ? 'regional' : 'nacional'))

  /** Resumen de coberturas de los vehículos: 'Nacional' o 'Nacional / Valle…' si varían. */
  const coberturaStr = () => {
    const u = [...new Set(vehiculosCob.map((g) => String(g.cobertura || 'Nacional').trim()))]
    return u.length ? u.join(' / ') : 'Nacional'
  }

  /** Qué le va a pasar al precio en el sitio. Es la política del documento, resuelta en pantalla. */
  const efecto = useMemo(() => {
    if (!estrategiaSel) return null
    return coberturaTipo() === 'nacional' ? estrategiaSel.efecto_nacional : estrategiaSel.efecto_regional
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estrategiaSel, vehiculosCob])

  // Paso 3: «Hasta» antes de «Desde» no se guarda; si está bien, cuántos días le quedan al bono.
  // F105: con horas, el mismo día también puede quedar al revés (hasta 08:00 antes que desde 10:00).
  const fechasAlReves = Boolean(f.desde && f.hasta && (f.hasta < f.desde
    || (f.hasta === f.desde && f.hora_desde && f.hora_hasta && f.hora_hasta <= f.hora_desde)))
  const diasRestantesTxt = (() => {
    if (!f.hasta) return ' '
    const hoy = new Date()
    const fin = new Date(`${f.hasta}T${f.hora_hasta || '23:59'}:59`)
    const dias = Math.ceil((fin - hoy) / 86400000)
    if (dias < 0) return 'Ya venció.'
    return dias === 0 ? 'Vence hoy.' : `Quedan ${dias} día${dias === 1 ? '' : 's'}.`
  })()

  /** F98/F99: las motos que aún no están en Lucia, con lo de su paso 4, como las espera el back. */
  const pendientesAGuardar = () => pendLucia.map((p) => ({
    material: p.material,
    vehiculo: p.vehiculo,
    cobertura: String(p.cobertura || 'Nacional').trim(),
    legal: String(p.legal || '').trim(),
    img_url: String(p.img_url || '').trim(),
    precio_antes: num(p.precio_antes),
  }))

  /** Guarda las motos que cambiaron o que no tenían cobertura propia. Devuelve el texto de lo que falló. */
  const guardarCobertura = async (reglaId) => {
    const items = filasCob.filter((x) => x.cambiada || x.sinCoberturaPropia)
      .map((x) => ({
        material: x.material,
        cobertura: String(x.cobertura).trim(),
        legal: String(x.legal).trim(),
        img_url: String(x.img_url || '').trim(),
        // Siempre va: el back guarda todos los campos de la moto y sin esto borraría el precio antes.
        precio_antes: num(x.precio_antes),
      }))
    if (!items.length) return ''
    const r = await setBonoMateriales(reglaId, items)
    if (!r) return 'no se pudo guardar la cobertura por moto'
    const fallas = (r.data || []).filter((x) => x.error)
    return fallas.map((x) => `${x.material}: ${x.error}`).join(' · ')
  }

  const cargarEdicion = (row) => {
    setEditId(row.id)
    // F63: precarga los materiales actuales del bono desde materiales_csv para poder quitarlos o
    // agregar otros con el mismo selector del alta. Los que no estén en el catálogo con cambio se
    // representan con lo mínimo (el código) para que igual se vean y se puedan sacar.
    const codigos = String(row.materiales_csv || '').split(',').map((c) => c.trim()).filter(Boolean)
    const mats = codigos.map((cod) => materiales.find((m) => String(m.material) === cod) || { material: cod })
    const claves = new Set(mats.map((m) => `${m.marca || ''}|${m.modelo || ''}|${m.anio_modelo || ''}`))
    setSelMotos(motosOpt.filter((mo) => claves.has(mo.clave)))
    setSelMats(mats)
    setPendLucia(pendientesLuciaDe(row))
    setLegalComun(false)
    setEsBorrador(String(row.borrador) === '1')
    setF({
      material: '',
      modelo_texto: row.modelo || '',
      nombre_promo: row.nombre_promo || '',
      tipo: row.tipo || 'progreser',
      financiera: row.financiera || '',
      monto: row.monto != null ? String(row.monto) : '',
      auteco: row.split_auteco != null ? String(row.split_auteco) : '',
      aporte_financiera: row.split_financiera != null ? String(row.split_financiera) : '',
      beneficio: row.beneficio_detalle || row.beneficio_especie || row.beneficio || '',
      etiqueta: row.etiqueta_promocion || '',
      estrategia: row.estrategia || '',
      desde: row.desde || '',
      hora_desde: row.hora_desde || '',
      hora_hasta: row.hora_hasta || '',
      hasta: row.hasta || '',
      legal: row.legal || '',
      img_url: row.img_url || '',
    })
  }

  /** Ver y editar abren el mismo panel con el bono cargado; ver lo deja en solo lectura. */
  const abrirBono = async (row, modoInicial) => {
    cargarEdicion(row)
    setEstadoBono(row.estado || '')
    setModo(modoInicial)
    const r = await getBonoMateriales(row.id)
    const mapa = {}
    ;((r && r.success && r.data) || []).forEach((x) => { mapa[x.material] = x })
    setCobServer(mapa)
    setCobEdits({})
  }

  /** F103: publica el borrador abierto tal cual (desde ahí sale en la API). */
  const publicarActual = async () => {
    if (!editId) return
    const r = await publicarBono(editId)
    if (r && r.success) {
      toast.success('Bono publicado: ya sale en la API. IA / Impulsa / Ecommerce quedan pendientes.')
      cerrarPanel()
      fetchData()
      onChanged?.()
    } else {
      toast.error((r && r.error) || 'No se pudo publicar el bono.')
    }
  }

  /** F103: duplica el bono como borrador y lo abre para editarlo. */
  const duplicar = async (row) => {
    const r = await duplicarBono(row.id)
    if (!r || !r.success) {
      toast.error((r && r.error) || 'No se pudo duplicar el bono.')
      return
    }
    const pr = await getPromos()
    const lista = (pr && pr.success && pr.data) || []
    setPromos(lista)
    const nuevo = lista.find((p) => Number(p.id) === Number(r.id))
    toast.success('Copia creada como borrador: no sale en la API hasta que la publiques.')
    if (nuevo) abrirBono(nuevo, 'editar')
  }

  // publicar = true: después de guardar, el borrador pasa a publicado (F103).
  const guardar = async (publicar = false) => {
    // El texto principal del bono es su nombre: es lo que ve el comprador (el «bono» de TP).
    if (!String(f.nombre_promo || '').trim()) {
      toast.error('Escribe el nombre de la promoción.')
      return
    }
    if (pideFinanciera && !String(f.financiera || '').trim()) {
      toast.error('Indica la financiera del bono.')
      return
    }
    // Un bono viejo puede traer textos más largos que el límite: hay que acortarlos antes de guardar.
    const largos = [['Nombre de la promoción', f.nombre_promo, LIMITES.nombre_promo], ['Detalle de beneficio', f.beneficio, LIMITES.beneficio],
      ['Etiqueta promoción', f.etiqueta, LIMITES.etiqueta]].filter(([, v, max]) => largo(v) > max)
    if (largos.length) {
      toast.error(`Acorta: ${largos.map(([n, v, max]) => `${n} (${largo(v)}/${max})`).join(', ')}.`)
      return
    }
    if (fechasAlReves) {
      toast.error('La fecha «Hasta» es anterior a «Desde».')
      return
    }
    // F84: en precio estructural cada vehículo necesita su precio antes (el que se muestra tachado).
    if (esEstructural) {
      const faltan = vehiculosCob.filter((g) => !num(g.precio_antes)).map((g) => g.etiqueta)
      if (faltan.length) {
        toast.error(`Falta el precio antes de: ${faltan.join(', ')}.`)
        return
      }
      // F93: el precio de ahora es el de SAP; el de antes tiene que ser mayor (si SAP ya tiene precio).
      const noBaja = gruposCob.filter((g) => Number(g.pvp_sap) > 0 && num(g.precio_antes) <= Number(g.pvp_sap)).map((g) => g.etiqueta)
      if (noBaja.length) {
        toast.error(`El precio antes debe ser mayor al precio de SAP en: ${noBaja.join(', ')}.`)
        return
      }
    }
    const cobertura = coberturaStr()
    const legal = f.legal || plantillaLegal(f, cobertura)

    // Modo edición: actualiza campos del bono existente
    if (editId) {
      // F63: un bono editado sigue necesitando al menos un material, igual que en el alta (F98: o una
      // moto que aún no está en Lucia).
      if (!selMats.length && !pendLucia.length) {
        toast.error('Selecciona al menos un vehículo, o agrega una moto que aún no está en Lucia.')
        return
      }
      const res = await actualizarPromo({
        id: editId, modelo_texto: modeloAGuardar(), tipo: f.tipo, financiera: f.financiera,
        monto: montoAGuardar(), ...repartoAGuardar(), cobertura,
        // F62: al editar tambien se guardan estrategia y cobertura. Si viene estrategia, el back
        // pasa por el update nuevo que las persiste en el detalle.
        estrategia: f.estrategia || '', cobertura_tipo: coberturaTipo(),
        // F92: textos del bono que entrega la API (beneficio, tag_vtex).
        beneficio_detalle: f.beneficio || '', etiqueta_promocion: f.etiqueta || '',
        // F63: el back ahora acepta materiales en el PUT; mismo shape que registrarPromo.
        materiales: selMats.map((m) => m.material),
        // F98: motos que aún no están en el catálogo (se reemplaza la lista del bono).
        pendientes: pendientesAGuardar(),
        desde: f.desde, hasta: f.hasta, legal, img_url: '', nombre_promo: f.nombre_promo, // F73: la pieza es por moto (paso 4)
        hora_desde: f.hora_desde || '', hora_hasta: f.hora_hasta || '', // F105
      })
      if (res && res.success && res.id > 0) {
        // Después del bono (que pudo agregar o quitar motos), la cobertura por moto.
        const fallaCob = await guardarCobertura(editId)
        if (res.aviso) toast.warn(`Bono actualizado, pero: ${res.aviso}`)
        if (fallaCob) toast.warn(`Bono actualizado, pero la cobertura de algunas motos no quedó: ${fallaCob}`)
        else if (!publicar) {
          toast.success(esBorrador
            ? 'Borrador guardado. No sale en la API hasta que lo publiques.'
            : 'Bono actualizado. IA / Impulsa / Ecommerce quedan pendientes de reflejar el cambio.')
        }
        if (publicar) {
          const pb = await publicarBono(editId)
          if (pb && pb.success) toast.success('Bono publicado: ya sale en la API.')
          else toast.error(`Se guardó, pero no se publicó: ${(pb && pb.error) || 'error'}`)
        }
        cerrarPanel()
        fetchData()
        onChanged?.()
      } else {
        toast.error((res && res.error) || 'No se pudo actualizar el bono.')
        // Los datos del bono quedaron pero los materiales no: se recarga para que el listado muestre
        // lo que de verdad hay guardado. El formulario queda abierto para reintentar.
        if (res && res.parcial) {
          fetchData()
          onChanged?.()
        }
      }
      return
    }

    // Modo registro: crea el bono para los materiales seleccionados (F98: o motos que aún no están en Lucia)
    if (!selMats.length && !pendLucia.length) {
      toast.error('Selecciona al menos un vehículo, o agrega una moto que aún no está en Lucia.')
      return
    }
    // La estrategia es obligatoria: es la estructura nueva. Sin ella el alta caería al camino viejo.
    if (!f.estrategia) {
      toast.error('Elige la estrategia del bono.')
      return
    }
    const res = await registrarPromo({
      materiales: selMats.map((m) => m.material),
      modelo_texto: modeloAGuardar(), tipo: f.tipo, financiera: f.financiera,
      monto: montoAGuardar(), ...repartoAGuardar(), cobertura,
      estrategia: f.estrategia || '', cobertura_tipo: coberturaTipo(),
        // F92: textos del bono que entrega la API (beneficio, tag_vtex).
        beneficio_detalle: f.beneficio || '', etiqueta_promocion: f.etiqueta || '',
      beneficio: f.beneficio || f.nombre_promo || '',
      // F98: motos que aún no están en el catálogo.
      pendientes: pendientesAGuardar(),
      desde: f.desde, hasta: f.hasta, legal, img_url: '', nombre_promo: f.nombre_promo, // F73: la pieza es por moto (paso 4)
      hora_desde: f.hora_desde || '', hora_hasta: f.hora_hasta || '', // F105
    })
    if (res && res.success && res.id > 0) {
      // Las motos ya quedaron en el bono: ahora la cobertura/términos que se pusieron por moto.
      const fallaCob = await guardarCobertura(res.id)
      if (res.aviso) toast.warn(`Bono publicado, pero: ${res.aviso}`)
      if (fallaCob) toast.warn(`Bono publicado, pero la cobertura de algunas motos no quedó: ${fallaCob}`)
      else toast.success(`Bono publicado para ${selMats.length} material(es)${pendLucia.length ? ` y ${pendLucia.length} esperando catálogo` : ''}. IA / Impulsa / Ecommerce quedan pendientes.`)
      cerrarPanel()
      fetchData()
      onChanged?.()
    } else {
      toast.error((res && res.error) || 'No se pudo publicar el bono.')
    }
  }

  // "Sin bono": el bono es opcional. Marca materiales sin promoción para que el flujo no se frene.
  const marcarSin = async () => {
    if (!selMats.length) {
      toast.error('Selecciona al menos un material para marcar sin bono.')
      return
    }
    const modelo = f.modelo_texto || (selMats[0] ? `${selMats[0].marca} ${selMats[0].modelo}` : '')
    const res = await marcarSinBono({ materiales: selMats.map((m) => m.material), modelo_texto: modelo })
    if (res && res.success) {
      toast.success(`${selMats.length} material(es) marcados sin bono. Se avisó a Back Comercial para publicar el plano.`)
      cerrarPanel()
      onChanged?.()
    } else {
      toast.error('No se pudo marcar sin bono.')
    }
  }

  const retirar = async (id) => {
    const res = await retirarPromo(id)
    if (res && res.success) {
      toast.success('Bono retirado.')
      if (editId === id) cerrarPanel()
      fetchData()
      onChanged?.()
    } else {
      toast.error('No se pudo retirar.')
    }
  }

  /** Retirar saca el bono del sitio: se confirma, diciendo cuál. */
  const confirmarRetiro = (r) => {
    const borrador = String(r.borrador) === '1'
    Modal.confirm({
      title: borrador ? 'Descartar este borrador' : 'Retirar este bono',
      content: borrador
        ? `${r.nombre_promo || 'Borrador'}. Se descarta sin haber salido en el sitio.`
        : `${r.nombre_promo || 'Bono'} · ${r.modelo || ''}. Deja de mostrarse en el sitio para sus ${
          String(r.material || '').includes('materiales') ? r.material : '1 material'}.`,
      okText: borrador ? 'Sí, descartar' : 'Sí, retirar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () => retirar(r.id),
    })
  }

  const columns = useMemo(
    () => {
      const cols = [
        {
          // El bono: el nombre con el que sale en el sitio y la estrategia que define su efecto.
          id: 'nombre_promo',
          header: 'Bono',
          size: 250,
          // El buscador encuentra por nombre, estrategia, financiera, etiqueta y detalle de beneficio.
          accessorFn: (r) => [r.nombre_promo, r.estrategia_nombre, r.financiera, r.etiqueta_promocion, r.beneficio_detalle]
            .filter(Boolean).join(' '),
          Cell: ({ row }) => {
            const t = row.original.nombre_promo || ''
            const est = row.original.estrategia_nombre || ''
            // F92: la etiqueta del bono; si no tiene, la de la estrategia (igual que la API).
            const e = estrategias.find((x) => x.clave === row.original.estrategia)
            const tag = row.original.etiqueta_promocion || (e && e.tag_vtex) || ''
            return (
              <span title={t}>
                {String(row.original.borrador) === '1' && (
                  <span style={{ fontSize: 9.5, fontWeight: 800, color: '#8a5a00', background: '#fff3d6', borderRadius: 4, padding: '1px 5px', marginRight: 5 }}>
                    BORRADOR
                  </span>
                )}
                <span style={{ fontSize: 12, fontWeight: 600, color: '#0f2439' }}>{t || '—'}</span>
                <div style={{ fontSize: 10.5, color: est ? '#6b7684' : '#d0a24a' }}>
                  {est || 'sin estrategia'}
                  {row.original.financiera && <span> · {row.original.financiera}</span>}
                  {row.original.efecto === 'tacha' && <b style={{ color: '#8a5a00' }}> · TACHA</b>}
                </div>
                {tag && <div style={{ fontSize: 10.5, color: '#6b7684' }}>Etiqueta: <b>{tag}</b></div>}
                {row.original.beneficio_detalle && (
                  <div style={{ fontSize: 10.5, color: '#6b7684' }}>Beneficio: {row.original.beneficio_detalle}</div>
                )}
              </span>
            )
          },
        },
        {
          // A qué vehículos aplica (modelo + año) y cuántos colores de cada uno.
          id: 'vehiculos',
          header: 'Vehículos',
          size: 220,
          // También por código de material (los del bono y los que esperan catálogo).
          accessorFn: (r) => [...vehiculosDe(r).map((v) => `${v.vehiculo} ${v.anio || ''}`),
            ...pendientesLuciaDe(r).map((p) => `${p.vehiculo} ${p.material}`),
            String(r.materiales_csv || '').replace(/,/g, ' ')].join(' · '),
          Cell: ({ row }) => {
            const vs = vehiculosDe(row.original)
            const pend = pendientesDe(row.original)
            const pendL = pendientesLuciaDe(row.original)
            if (!vs.length && !pend.length && !pendL.length) return <span style={{ color: '#9aa5b1' }}>{row.original.modelo || '—'}</span>
            // F98: las cargadas desde Lucia, por vehículo (varios códigos del mismo = una línea).
            const pendLPorVeh = [...pendL.reduce((m, p) => {
              const k = String(p.vehiculo || '').trim().toUpperCase()
              m.set(k, (m.get(k) || 0) + 1)
              return m
            }, new Map()).entries()]
            const lineas = [
              ...vs.map((v) => ({
                k: `${v.vehiculo}-${v.anio}`,
                txt: `${v.vehiculo} ${v.anio || ''}`.trim(),
                extra: `${v.colores} color${Number(v.colores) === 1 ? '' : 'es'}`,
              })),
              // F88: de TP, todavía no están en el catálogo de Lucia; entran solas cuando lleguen.
              ...pend.map((p) => ({
                k: `pend-${p.vehiculo}`,
                txt: p.vehiculo,
                extra: `${p.colores.length} color${p.colores.length === 1 ? '' : 'es'} · esperando catálogo`,
                espera: true,
              })),
              ...pendLPorVeh.map(([veh, n]) => ({
                k: `pendl-${veh}`, txt: veh, extra: `${n} código${n === 1 ? '' : 's'} · esperando catálogo`, espera: true,
              })),
            ]
            // Filas cortas: hasta 3 vehículos y «+N más»; al pasar el mouse se ven todos.
            const MAX = 3
            const todos = lineas.map((l) => `${l.txt} · ${l.extra}`).join('\n')
            return (
              <div title={lineas.length > MAX ? todos : undefined}>
                {lineas.slice(0, MAX).map((l) => (
                  <div key={l.k} style={{ fontSize: 11.5, lineHeight: 1.45, color: l.espera ? '#8a5a00' : undefined }}>
                    {l.txt}
                    <span style={{ color: l.espera ? '#8a5a00' : '#8a94a0' }}> · {l.extra}</span>
                  </div>
                ))}
                {lineas.length > MAX && (
                  <div style={{ fontSize: 11, color: '#2f5b8f', fontWeight: 600 }}>+{lineas.length - MAX} más</div>
                )}
              </div>
            )
          },
        },
        {
          id: 'monto',
          header: 'Valor',
          size: 110,
          accessorFn: (r) => Number(r.monto || 0),
          Cell: ({ row }) => {
            // Los bonos en especie (SOAT, maletero, mercado) no llevan plata: el SP devuelve monto NULL
            // por diseño (CASE r.valor IS NULL). fmt(null) pintaba «$0», que engaña. Se muestra el
            // beneficio entregado (valor_texto → beneficio_especie) o, si no vino, un chip «en especie».
            const m = row.original.monto
            if (m == null || m === '') {
              const esp = String(row.original.beneficio_especie || '').trim()
              return esp
                ? <span style={{ fontSize: 11.5 }}>{esp}</span>
                : chip('en especie', '#eef6ff', '#1c4a80')
            }
            // Un solo valor: el reparto Auteco / financiera sale de la estrategia y no se lista.
            return <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(row.original.monto)}</b>
          },
        },
        {
          // Por vehículo: Nacional si todos lo son; si no, cuáles vehículos son regionales.
          id: 'cobertura',
          header: 'Cobertura',
          size: 130,
          accessorFn: (r) => r.cobertura || '',
          Cell: ({ row }) => {
            const vs = vehiculosDe(row.original)
            if (!vs.length) return String(row.original.cobertura || '—')
            if (vs.every((v) => esNacional(v.cobertura))) return chip('Nacional', '#e6f6ec', '#166534')
            return (
              <div>
                {vs.map((v) => (
                  <div key={`${v.vehiculo}-${v.anio}`} style={{ fontSize: 11, lineHeight: 1.45 }} title={v.cobertura}>
                    <span style={{ color: '#8a94a0' }}>{v.anio || ''}: </span>
                    {esNacional(v.cobertura)
                      ? 'Nacional'
                      : <span style={{ color: '#1c4a80' }}>{String(v.cobertura || '').split(', ')[0]}{String(v.cobertura || '').split(', ').length > 1 ? ` +${String(v.cobertura).split(', ').length - 1}` : ''}</span>}
                  </div>
                ))}
              </div>
            )
          },
        },
        {
          // Vigencia y estado en una sola columna (eran dos y ensanchaban la tabla).
          id: 'vigencia',
          header: 'Vigencia',
          size: 140,
          accessorFn: (r) => `${r.desde || '—'} → ${r.hasta || '—'} ${r.estado || ''}`,
          Cell: ({ row }) => (
            <div style={{ fontSize: 11.5, lineHeight: 1.45 }}>
              <div style={{ fontVariantNumeric: 'tabular-nums' }}>
                {row.original.desde || '—'}{row.original.hora_desde ? <span style={{ color: '#6b7684' }}> {row.original.hora_desde}</span> : null}
              </div>
              <div style={{ fontVariantNumeric: 'tabular-nums' }}>
                → {row.original.hasta || '—'}{row.original.hora_hasta ? <span style={{ color: '#6b7684' }}> {row.original.hora_hasta}</span> : null}
              </div>
              {String(row.original.borrador) === '1'
                ? chip('Borrador', '#fff3d6', '#8a5a00')
                : (row.original.estado === 'vigente'
                  ? chip('Vigente', '#e6f6ec', '#166534')
                  : chip('Vencido', '#fdecec', '#8f2d2d'))}
            </div>
          ),
        },
      ]
      // Ver: todos (Back Comercial valida leyendo). Editar y Retirar: solo quien edita (ProgreSER/admin).
      const accion = { minWidth: 0, px: 0.75 }
      cols.push(colAcciones((r) => (
        // stopPropagation: el clic en un botón no debe abrir además el bono por el clic de la fila.
        <span style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()} role="presentation">
          <Button size="small" sx={accion} onClick={() => abrirBono(r, 'ver')}>Ver</Button>
          {!readOnly && <Button size="small" sx={accion} onClick={() => abrirBono(r, 'editar')}>Editar</Button>}
          {/* F103: copia como borrador para editarla y publicarla después. */}
          {!readOnly && <Button size="small" sx={accion} onClick={() => duplicar(r)}>Duplicar</Button>}
          {!readOnly && <Button size="small" sx={accion} color="error" onClick={() => confirmarRetiro(r)}>Retirar</Button>}
        </span>
      ), readOnly ? 70 : 190))
      return cols
    },
    // materiales/motosOpt: abrirBono precarga las motos con el catálogo; con solo [readOnly] el botón
    // se quedaba con el catálogo del primer render (vacío) y las motos llegaban sin modelo ni color.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [readOnly, materiales, motosOpt, estrategias],
  )

  /** Columnas de «Ver por material»: la moto primero y su bono al lado. */
  const columnsMaterial = useMemo(() => [
    {
      id: 'material',
      header: 'Material',
      size: 150,
      accessorFn: (r) => `${r.material} ${r.color}`,
      Cell: ({ row }) => (
        <div style={{ fontSize: 11.5, lineHeight: 1.45 }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.original.material}</span>
          {row.original.color && <div style={{ color: '#6b7684' }}>{row.original.color}</div>}
        </div>
      ),
    },
    {
      id: 'vehiculo',
      header: 'Vehículo',
      size: 210,
      accessorFn: (r) => r.vehiculo,
      Cell: ({ row }) => (
        <div style={{ fontSize: 11.5, lineHeight: 1.45 }}>
          {row.original.vehiculo}
          {row.original.espera && (
            <div style={{ color: '#8a5a00' }}>esperando catálogo{row.original.espera === 'TP' ? ' · de TP' : ''}</div>
          )}
        </div>
      ),
    },
    {
      id: 'bono',
      header: 'Bono',
      size: 250,
      accessorFn: (r) => [r.bono.nombre_promo, r.bono.estrategia_nombre, r.bono.financiera, r.bono.etiqueta_promocion].filter(Boolean).join(' '),
      Cell: ({ row }) => {
        const p = row.original.bono
        return (
          <span>
            {String(p.borrador) === '1' && (
              <span style={{ fontSize: 9.5, fontWeight: 800, color: '#8a5a00', background: '#fff3d6', borderRadius: 4, padding: '1px 5px', marginRight: 5 }}>BORRADOR</span>
            )}
            <span style={{ fontSize: 12, fontWeight: 600, color: '#0f2439' }}>{p.nombre_promo || '—'}</span>
            <div style={{ fontSize: 10.5, color: '#6b7684' }}>
              {p.estrategia_nombre || 'sin estrategia'}{p.financiera ? ` · ${p.financiera}` : ''}
              {p.efecto === 'tacha' && <b style={{ color: '#8a5a00' }}> · TACHA</b>}
            </div>
          </span>
        )
      },
    },
    {
      id: 'monto',
      header: 'Valor',
      size: 110,
      accessorFn: (r) => Number(r.bono.monto || 0),
      Cell: ({ row }) => {
        const m = row.original.bono.monto
        return m == null || m === ''
          ? <span style={{ fontSize: 11.5 }}>{row.original.bono.beneficio_especie || 'en especie'}</span>
          : <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(m)}</b>
      },
    },
    {
      id: 'vigencia',
      header: 'Vigencia',
      size: 140,
      accessorFn: (r) => `${r.bono.desde || ''} ${r.bono.hasta || ''} ${estadoDe(r.bono)}`,
      Cell: ({ row }) => {
        const p = row.original.bono
        const est = estadoDe(p)
        return (
          <div style={{ fontSize: 11.5, lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' }}>
            <div>{p.desde || '—'}{p.hora_desde ? ` ${p.hora_desde}` : ''}</div>
            <div>→ {p.hasta || '—'}{p.hora_hasta ? ` ${p.hora_hasta}` : ''}</div>
            {est === 'borrador' ? chip('Borrador', '#fff3d6', '#8a5a00')
              : (est === 'vigente' ? chip('Vigente', '#e6f6ec', '#166534') : chip('Vencido', '#fdecec', '#8f2d2d'))}
          </div>
        )
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [promos])

  /**
   * Lo que se va a publicar, tal cual lo pinta el sitio (docs/preview-bonos.png): mismos campos que la API.
   * Debajo, lo de administración que el sitio no muestra (estrategia, efecto, vigencia, vehículos).
   */
  const preview = (
    // Fija al bajar por el formulario: se ve el resultado mientras se llenan los pasos 2 a 4.
    <div style={{ alignSelf: 'flex-start', minWidth: 300, position: 'sticky', top: 72 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0f2439', marginBottom: 6 }}>Así se ve en el sitio</div>
      <PreviewBono
        filas={filasCob}
        nombre={f.nombre_promo}
        beneficio={f.beneficio}
        efecto={efecto}
        monto={montoNum}
        financiera={f.financiera}
        coberturaTipo={coberturaTipo()}
        etiqueta={f.etiqueta || (estrategiaSel && estrategiaSel.tag_vtex) || ''}
        legal={f.legal || (filasCob.find((x) => x.legal) || {}).legal || ''}
        esEstructural={esEstructural}
      />
      <div style={{ fontSize: 11.5, color: '#6b7684', marginTop: 10, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px', maxWidth: 300 }}>
        <span>Estrategia</span>
        <b style={{ color: '#0f2439' }}>{estrategiaSel ? estrategiaSel.nombre : <span style={{ color: '#c9ced4', fontWeight: 400 }}>sin elegir</span>}</b>
        <span>Efecto</span>
        <b style={{ color: efecto === 'tacha' ? '#8a5a00' : '#0f2439' }}>
          {EFECTO_CORTO[efecto] || <span style={{ color: '#c9ced4', fontWeight: 400 }}>se define con la estrategia y la cobertura</span>}
        </b>
        <span>Vigencia</span>
        <b style={{ color: '#0f2439' }}>
          {f.desde || '—'}{f.hora_desde ? ` ${f.hora_desde}` : ''} → {f.hasta || '—'}{f.hora_hasta ? ` ${f.hora_hasta}` : ''}
        </b>
        <span>Vehículos</span>
        <b style={{ color: selMats.length || pendLucia.length ? '#1c4a80' : '#d64545' }}>
          {selMats.length || pendLucia.length
            ? [selMats.length ? `${gruposCob.length} vehículo(s) · ${selMats.length} color(es)` : '',
              gruposPend.length ? `${gruposPend.length} esperando catálogo` : ''].filter(Boolean).join(' · ')
            : 'Falta elegir los vehículos'}
        </b>
      </div>
    </div>
  )

  return (
    <div>
      {/* Crear, ver y editar: el MISMO panel y el mismo formulario. 'ver' lo deja en solo lectura. */}
      <Drawer
        anchor="right"
        open={Boolean(modo)}
        onClose={cerrarPanel}
        PaperProps={{ sx: { width: { xs: '100vw', md: 'min(1180px, 94vw)' }, background: '#f7f9fb' } }}
      >
        <div
          style={{
            position: 'sticky', top: 0, zIndex: 2, background: '#fff', borderBottom: '1px solid #e3e7ec',
            padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f2439' }}>
              {modo === 'crear' && 'Nuevo bono / promoción'}
              {modo === 'editar' && (esBorrador ? 'Editar borrador' : 'Editar bono')}
              {modo === 'ver' && (f.nombre_promo || 'Bono')}
            </div>
            {modo !== 'crear' && (
              <div style={{ fontSize: 12, color: '#6b7684', marginTop: 2 }}>
                {modeloAGuardar()} · {selMats.length} moto(s)
                {esBorrador && <span style={{ marginLeft: 8 }}>{chip('Borrador · no sale en la API', '#fff3d6', '#8a5a00')}</span>}
                {!esBorrador && estadoBono && <span style={{ marginLeft: 8 }}>
                  {estadoBono === 'vigente' ? chip('Vigente', '#e6f6ec', '#166534') : chip('Vencido', '#fdecec', '#8f2d2d')}
                </span>}
              </div>
            )}
          </div>
          {modo === 'ver' && !readOnly && (
            <Button size="small" variant="contained" onClick={() => setModo('editar')}>Editar</Button>
          )}
          <IconButton onClick={cerrarPanel} aria-label="Cerrar" size="small">✕</IconButton>
        </div>

        <div style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div className="precios-card" style={{ flex: '1 1 560px', minWidth: 420, margin: 0 }}>
              <Paso
                n={1}
                titulo="¿A qué vehículos aplica?"
                ayuda="Al agregar un vehículo entran todos sus colores; en su tarjeta puedes quitar los que no aplican. Si la moto aún no está en Lucia, agrégala por código más abajo."
              >
                {/* F63: el mismo selector del alta, ahora también al editar: se pueden quitar o
                    agregar materiales. En edición viene precargado desde materiales_csv. */}
                {/* 1) La MOTO. Lista corta y etiqueta simple: es lo que hace que no se trabe. */}
                <Autocomplete
                  multiple
                  size="small"
                  options={motosOpt}
                  filterOptions={filtrarMotos}
                  getOptionLabel={(m) => m.etiqueta}
                  isOptionEqualToValue={(a, b) => a.clave === b.clave}
                  value={selMotos}
                  disabled={soloVer}
                  onChange={(_, v) => elegirMotos(v)}
                  // los elegidos se ven en las tarjetas de abajo, no como fichas dentro del campo
                  renderTags={() => null}
                  disableClearable
                  renderOption={(props, m) => (
                    <li {...props} key={m.clave}>
                      {m.etiqueta}
                      <span style={{ color: '#8a94a0', fontSize: 12, marginLeft: 6 }}>
                        · {m.mats.length} color(es)
                      </span>
                      {m.sinPrecio && <span style={{ color: '#b7791f', fontSize: 11.5, marginLeft: 6 }}>· sin precio de Trade</span>}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={selMotos.length ? `Agregar otro vehículo (${selMotos.length} elegido${selMotos.length === 1 ? '' : 's'})` : 'Agregar vehículo'}
                      placeholder="Busca por marca, modelo, color o código de material"
                    />
                  )}
                />

                {/* 2) Cada vehículo en su tarjeta con SUS colores (mismo orden y numeración que el paso 4). */}
                <VehiculosDelBono
                  vehiculos={selMotos}
                  incluidos={incluidos}
                  sueltos={sueltos}
                  // F98/F99: las motos que aún no están en Lucia, también en tarjetas por vehículo.
                  pendientes={pendientesTarjetas}
                  editable={!soloVer}
                  onToggle={alternarColor}
                  onQuitar={quitarVehiculo}
                  onQuitarPendiente={(cod) => setPendLucia((prev) => prev.filter((x) => x.material !== cod))}
                  onQuitarVehiculoPendiente={(clave) => setPendLucia((prev) => prev.filter(
                    (x) => `pend:${String(x.vehiculo || '').trim().toUpperCase()}` !== clave,
                  ))}
                />
                {/* F98: motos que aún no están en el catálogo de Lucia. Temporal: el bono ya sale en la API
                    para ese material (esperando catálogo) y la moto entra sola cuando llega de SAP. */}
                {!soloVer && (
                  <div style={{ marginTop: 10, padding: '9px 12px', borderRadius: 8, border: '1px dashed #c9d3df', background: '#fafcff' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0f2439' }}>¿La moto aún no está en Lucia?</div>
                    <div style={{ fontSize: 11.5, color: '#6b7684', marginTop: 2 }}>
                      Agrégala con su código de material. Varios códigos con el mismo vehículo quedan en una sola
                      tarjeta. Sale en la API como «esperando catálogo» y entra sola al bono cuando llegue de SAP.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '150px minmax(0, 1fr) auto', gap: 8, marginTop: 8, alignItems: 'start' }}>
                      <TextField
                        size="small"
                        label="Código de material"
                        placeholder="60006652"
                        value={pendCod}
                        onChange={(e) => setPendCod(e.target.value.replace(/\D/g, '').slice(0, 12))}
                        inputProps={{ inputMode: 'numeric' }}
                      />
                      {/* Sugiere los vehículos pendientes del bono: otro color del mismo cae en su tarjeta. */}
                      <Autocomplete
                        freeSolo
                        size="small"
                        options={gruposPend.map((g) => g.clave.slice(5))}
                        inputValue={pendVeh}
                        onInputChange={(_, v) => setPendVeh(String(v || '').slice(0, 200))}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Vehículo"
                            placeholder="VICTORY MRX 150 2027"
                            onKeyDown={(e) => { if (e.key === 'Enter') agregarPendiente() }}
                          />
                        )}
                      />
                      <Button variant="outlined" size="small" sx={{ height: 40 }} onClick={agregarPendiente}>Agregar</Button>
                    </div>
                  </div>
                )}
                {/* El texto principal del bono: el «bono» de TP, lo que ve el comprador. El modelo o
                    referencia ya no se pide: son los vehículos elegidos arriba. */}
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Nombre de la promoción"
                  placeholder="BONO $500.000 FINANCIANDO CON PROGRESER"
                  value={f.nombre_promo}
                  disabled={soloVer}
                  onChange={set('nombre_promo')}
                  error={!soloVer && (!String(f.nombre_promo || '').trim() || largo(f.nombre_promo) > LIMITES.nombre_promo)}
                  inputProps={{ maxLength: LIMITES.nombre_promo }}
                  helperText={`${cuenta(f.nombre_promo, LIMITES.nombre_promo)} · Es el texto principal del bono: lo que ve el comprador.`}
                  sx={{ mt: 1.5 }}
                />
              </Paso>

              <Paso
                n={2}
                titulo={usaSplit ? '¿Cuánto es y quién lo paga?' : '¿Cuánto es?'}
                ayuda={usaSplit
                  ? 'Lo que pone cada uno. El total que ve el cliente es la suma.'
                  : 'Un solo monto. Quién lo pone sale de la estrategia: comercial = Auteco, financiación = la financiera.'}
              >
                {/* Grilla fija de dos columnas: estrategia (fila entera) · financiera y montos · detalle y
                    etiqueta. Todas las ayudas van en una línea (ayudaUnaLinea) para que las celdas de una
                    misma fila queden de la misma altura con cualquier estrategia. */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '4px 12px', maxWidth: 640, alignItems: 'start' }}>
                  {/* La estrategia es la estructura del documento «PRECIOS Y PROMOCIONES EN MOTOS»: es
                      la que manda el efecto, el tag de VTEX y si lleva plata. Es obligatoria; la
                      clasificación vieja (Tipo de bono: progreser/marca/soat) se retiró del alta. */}
                  {estrategias.length > 0 && (
                    <TextField
                      select
                      required
                      size="small"
                      label="Estrategia"
                      value={f.estrategia || ''}
                      disabled={soloVer}
                      onChange={cambiarEstrategia}
                      error={!editId && !f.estrategia}
                      helperText={!editId && !f.estrategia ? 'Obligatoria: define el efecto y el tag de VTEX.' : ' '}
                      FormHelperTextProps={ayudaUnaLinea}
                      sx={{ width: '100%', gridColumn: '1 / -1' }}
                    >
                      {estrategias.map((e) => (
                        <MenuItem key={e.clave} value={e.clave}>{e.nombre}</MenuItem>
                      ))}
                    </TextField>
                  )}
                  {pideFinanciera && (
                    // Se elige de las que ya se usan o se escribe una nueva; se guarda en mayúscula, igual que TP.
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={financierasConocidas}
                      value={f.financiera || ''}
                      disabled={soloVer}
                      onChange={(_, v) => setF((prev) => ({ ...prev, financiera: String(v || '').toUpperCase().slice(0, LIMITES.financiera) }))}
                      onInputChange={(_, v, motivo) => {
                        if (motivo === 'input') setF((prev) => ({ ...prev, financiera: String(v || '').toUpperCase().slice(0, LIMITES.financiera) }))
                      }}
                      sx={{ width: '100%' }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="Financiera"
                          inputProps={{ ...params.inputProps, maxLength: LIMITES.financiera }}
                          error={!soloVer && !f.financiera}
                          helperText={!soloVer && !f.financiera ? 'Obligatoria en financiación.' : ' '}
                          FormHelperTextProps={ayudaUnaLinea}
                        />
                      )}
                    />
                  )}
                  {/* Auteco + financiación: el total no se escribe, es la suma; va al lado de la financiera
                      y los dos aportes en la fila de abajo. */}
                  {llevaMonto && usaSplit && (
                    <>
                      <TextField
                        size="small"
                        label="Total del bono"
                        value={fmt(totalSplit)}
                        disabled
                        helperText="Suma de los dos aportes."
                        FormHelperTextProps={ayudaUnaLinea}
                        sx={{ width: '100%' }}
                      />
                      <MoneyField label="Pone Auteco" value={f.auteco} disabled={soloVer} onChange={setNum('auteco')} helperText=" " FormHelperTextProps={ayudaUnaLinea} sx={{ width: '100%' }} />
                      <MoneyField label={`Pone ${f.financiera || 'la financiera'}`} value={f.aporte_financiera} disabled={soloVer} onChange={setNum('aporte_financiera')} helperText=" " FormHelperTextProps={ayudaUnaLinea} sx={{ width: '100%' }} />
                    </>
                  )}
                  {llevaMonto && !usaSplit && (
                    <MoneyField
                      // Precio estructural (f93): la baja real es por vehículo (precio antes − precio de SAP); este
                      // monto queda como referencia del bono.
                      label={esEstructural ? 'Baja de referencia' : 'Monto'}
                      value={f.monto}
                      disabled={soloVer}
                      onChange={setNum('monto')}
                      helperText={esEstructural
                        ? 'La baja real va por vehículo (paso 4).'
                        : `Lo pone ${pideFinanciera ? (f.financiera || 'la financiera') : 'Auteco'}.`}
                      FormHelperTextProps={ayudaUnaLinea}
                      sx={{ width: '100%' }}
                    />
                  )}
                  {/* Sin financiera el monto queda solo en su fila: la otra celda vacía mantiene las columnas. */}
                  {((llevaMonto && !usaSplit && !pideFinanciera) || (pideFinanciera && !llevaMonto)) && <div />}
                  {/* F92: textos del bono que entrega la API: beneficio y tag_vtex. En especie el detalle es
                      además lo que se entrega (obligatorio); en los demás es opcional. */}
                  <TextField
                    size="small"
                    required={!llevaMonto}
                    label="Detalle de beneficio"
                    placeholder={llevaMonto ? 'Ej: Bono aplica sobre el precio de la moto' : 'Casco, maletero, SOAT 1 año…'}
                    value={f.beneficio}
                    disabled={soloVer}
                    onChange={set('beneficio')}
                    inputProps={{ maxLength: LIMITES.beneficio }}
                    error={!soloVer && largo(f.beneficio) > LIMITES.beneficio}
                    helperText={`${cuenta(f.beneficio, LIMITES.beneficio)} · ${llevaMonto ? 'Opcional. En la API: «beneficio».' : 'Qué se entrega. En la API: «beneficio».'}`}
                    FormHelperTextProps={ayudaUnaLinea}
                    sx={{ width: '100%' }}
                  />
                  <TextField
                    size="small"
                    label="Etiqueta promoción"
                    placeholder={(estrategiaSel && estrategiaSel.tag_vtex) || 'Ej: BONO $XX FINANCIACIÓN'}
                    value={f.etiqueta}
                    disabled={soloVer}
                    onChange={set('etiqueta')}
                    // La etiqueta va a VTEX: máximo 31 caracteres.
                    inputProps={{ maxLength: LIMITES.etiqueta }}
                    error={!soloVer && largo(f.etiqueta) > LIMITES.etiqueta}
                    helperText={`${cuenta(f.etiqueta, LIMITES.etiqueta)} · En la API: «tag_vtex». Vacía = ${(estrategiaSel && estrategiaSel.tag_vtex) || 'la de la estrategia'}.`}
                    FormHelperTextProps={ayudaUnaLinea}
                    sx={{ width: '100%' }}
                  />
                </div>
                {efecto && (
                  <div
                    style={{
                      marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12.5,
                      background: efecto === 'tacha' ? '#fff8e8' : '#eef6ff',
                      border: `1px solid ${efecto === 'tacha' ? '#f0d089' : '#cfe2f7'}`,
                      color: efecto === 'tacha' ? '#8a5a00' : '#0f2439',
                    }}
                  >
                    <b>{coberturaTipo() === 'nacional' ? 'Nacional' : 'Regional'}</b>
                    {' · '}
                    {EFECTO_TXT[efecto]}
                    {(f.etiqueta || estrategiaSel.tag_vtex) && (
                      <div style={{ fontSize: 11.5, marginTop: 3, color: '#6b7684' }}>
                        Etiqueta promoción: <b>{f.etiqueta || estrategiaSel.tag_vtex}</b>
                      </div>
                    )}
                  </div>
                )}
              </Paso>

              <Paso n={3} titulo="¿Hasta cuándo?" ayuda="La vigencia es la del desembolso y vale para todos los vehículos del bono.">
                {/* F105: fecha + hora opcional (vacía = todo el día). La API respeta la hora el día de
                    inicio y el de fin (hora Colombia). */}
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <TextField
                      size="small"
                      type="date"
                      label="Desde"
                      InputLabelProps={{ shrink: true }}
                      value={f.desde}
                      disabled={soloVer}
                      onChange={set('desde')}
                      helperText=" "
                      sx={{ width: 165 }}
                    />
                    <TextField
                      size="small"
                      type="time"
                      label="Hora"
                      InputLabelProps={{ shrink: true }}
                      value={f.hora_desde}
                      disabled={soloVer}
                      onChange={set('hora_desde')}
                      helperText={f.hora_desde ? ' ' : 'Desde las 00:00'}
                      sx={{ width: 125 }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <TextField
                      size="small"
                      type="date"
                      label="Hasta"
                      InputLabelProps={{ shrink: true }}
                      value={f.hasta}
                      disabled={soloVer}
                      onChange={set('hasta')}
                      error={fechasAlReves}
                      helperText={fechasAlReves ? 'Es antes de «Desde».' : diasRestantesTxt}
                      sx={{ width: 165 }}
                    />
                    <TextField
                      size="small"
                      type="time"
                      label="Hora"
                      InputLabelProps={{ shrink: true }}
                      value={f.hora_hasta}
                      disabled={soloVer}
                      onChange={set('hora_hasta')}
                      error={fechasAlReves && f.hasta === f.desde}
                      helperText={f.hora_hasta ? ' ' : 'Hasta las 23:59'}
                      sx={{ width: 125 }}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 8, fontSize: 11.5, color: '#9aa5b1' }}>
                  La cobertura, los términos legales y la pieza gráfica van por vehículo, en el paso 4.
                </div>
              </Paso>

              {/* Por VEHÍCULO: uno solo (aunque tenga varios colores) = un legal, una pieza y una
                  cobertura. Varios vehículos = cada uno con los suyos. */}
              <Paso
                n={4}
                titulo={vehiculosCob.length > 1
                  ? `Términos legales, pieza y cobertura de cada vehículo (${vehiculosCob.length})`
                  : 'Términos legales, pieza y cobertura'}
                ayuda={vehiculosCob.length > 1
                  ? 'Cada vehículo lleva su propio legal, pieza y cobertura, que valen para todos sus colores. Cobertura vacía = la del paso 3; legal vacío = se genera con la vigencia y la cobertura.'
                  : 'Valen para todos los colores del vehículo. Legal vacío = se genera con la vigencia y la cobertura. Es lo que ve el sitio.'}
              >
                {/* Varios vehículos con el mismo legal: un check y se escribe una sola vez. */}
                {vehiculosCob.length > 1 && (
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10, padding: '6px 10px',
                      borderRadius: 8, background: legalComun ? '#eef6ff' : '#f7f9fb', border: `1px solid ${legalComun ? '#cfe2f7' : '#e3e7ec'}`,
                    }}
                  >
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={(
                        <Checkbox
                          size="small"
                          checked={legalComun}
                          disabled={soloVer}
                          onChange={(e) => alternarLegalComun(e.target.checked)}
                        />
                      )}
                      label={<span style={{ fontSize: 13, fontWeight: 600, color: '#0f2439' }}>Usar el mismo texto legal para todos los vehículos ({vehiculosCob.length})</span>}
                    />
                    <span style={{ fontSize: 11.5, color: '#6b7684' }}>
                      {legalComun
                        ? 'Lo que escribas en el legal de cualquier vehículo se copia a todos.'
                        : (legalesIguales ? 'Hoy todos tienen el mismo legal.' : 'Cada vehículo con su propio legal.')}
                    </span>
                  </div>
                )}
                <CoberturaPorMoto
                  // F99: también las motos que aún no están en Lucia (al final, marcadas «esperando catálogo»).
                  filas={vehiculosCob}
                  opcionesCobertura={coberturaOpts}
                  grupoCobertura={grupoCobertura}
                  legalSugeridoDe={(v) => plantillaLegal(f, v.cobertura || 'Nacional')}
                  editable={!soloVer}
                  onCambiar={cambiarCob}
                  pidePrecioAntes={esEstructural}
                  efecto={efecto}
                  monto={montoNum}
                  onSubirPieza={subirPiezaVehiculo}
                />
              </Paso>
            </div>

            {preview}
          </div>
        </div>

        <div
          style={{
            position: 'sticky', bottom: 0, background: '#fff', borderTop: '1px solid #e3e7ec',
            padding: '12px 20px', display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end',
          }}
        >
          {modo === 'ver' && (
            <>
              {!readOnly && editId && (
                <Button
                  variant="text"
                  color="error"
                  sx={{ mr: 'auto' }}
                  onClick={() => confirmarRetiro(promos.find((p) => p.id === editId) || { id: editId, nombre_promo: f.nombre_promo, modelo: f.modelo_texto })}
                >
                  {esBorrador ? 'Descartar borrador' : 'Retirar bono'}
                </Button>
              )}
              <Button variant="outlined" color="inherit" onClick={cerrarPanel}>Cerrar</Button>
              {!readOnly && <Button variant={esBorrador ? 'outlined' : 'contained'} onClick={() => setModo('editar')}>Editar</Button>}
              {/* F103: un borrador se publica tal cual desde aquí, o desde «Editar» después de ajustarlo. */}
              {!readOnly && esBorrador && <Button variant="contained" color="success" onClick={publicarActual}>Publicar</Button>}
            </>
          )}
          {modo === 'editar' && (
            <>
              {editId && (
                <Button
                  variant="text"
                  color="error"
                  sx={{ mr: 'auto' }}
                  onClick={() => confirmarRetiro(promos.find((p) => p.id === editId) || { id: editId, nombre_promo: f.nombre_promo, modelo: f.modelo_texto })}
                >
                  {esBorrador ? 'Descartar borrador' : 'Retirar bono'}
                </Button>
              )}
              <Button variant="outlined" color="inherit" onClick={cerrarPanel}>Cancelar</Button>
              <Button variant={esBorrador ? 'outlined' : 'contained'} onClick={() => guardar()}>
                {`${esBorrador ? 'Guardar borrador' : 'Guardar cambios'}${gruposCob.some((x) => x.cambiada) ? ` · ${gruposCob.filter((x) => x.cambiada).length} vehículo(s) con cambios` : ''}`}
              </Button>
              {/* F103: guarda lo editado y publica: desde ahí sale en la API. */}
              {esBorrador && <Button variant="contained" color="success" onClick={() => guardar(true)}>Guardar y publicar</Button>}
            </>
          )}
          {modo === 'crear' && (
            <>
              <Button variant="outlined" color="inherit" onClick={cerrarPanel}>Cancelar</Button>
              <Button variant="outlined" onClick={marcarSin}>Estas motos van sin bono</Button>
              <Button variant="contained" onClick={() => guardar()}>
                {`Publicar bono${selMats.length ? ` (${gruposCob.length} vehículo${gruposCob.length === 1 ? '' : 's'})` : ''}`}
              </Button>
            </>
          )}
        </div>
      </Drawer>

      <div className="precios-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div className="precios-section-title" style={{ marginBottom: 2 }}>
              {readOnly ? 'Bonos vigentes' : 'Bonos y promociones'}
            </div>
            <div style={{ fontSize: 12, color: '#6b7684' }}>
              {promos.length} bono(s) activo(s){readOnly ? ' · solo lectura' : ''}.
              {' «Ver» abre el detalle con la cobertura, el legal y la pieza de cada vehículo.'}
            </div>
          </div>
          {!readOnly && <Button variant="contained" onClick={abrirNuevo}>+ Nuevo bono</Button>}
        </div>
        {/* Filtros: estado (vigentes / vencidos) y estrategia, con cuántos bonos tiene cada uno. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
          {[{ clave: null, nombre: 'Todos', n: promos.length },
            { clave: 'vigente', nombre: 'Vigentes', n: nEstado('vigente') },
            { clave: 'vencido', nombre: 'Vencidos', n: nEstado('vencido') },
            ...(nEstado('borrador') ? [{ clave: 'borrador', nombre: 'Borradores', n: nEstado('borrador') }] : [])].map((o) => (
              <Button
                key={o.clave || '__todos'}
                size="small"
                variant={filtroEstado === o.clave ? 'contained' : 'outlined'}
                color={o.clave === 'vencido' ? 'error' : 'primary'}
                onClick={() => setFiltroEstado(o.clave)}
                sx={{ textTransform: 'none', borderRadius: 16, py: 0.25, fontSize: 12 }}
              >
                {o.nombre}
                <span style={{ marginLeft: 6, opacity: 0.75, fontVariantNumeric: 'tabular-nums' }}>{o.n}</span>
              </Button>
            ))}
          {/* Ver la tabla por bono o por material (una fila por moto de cada bono). */}
          <span style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7684' }}>
            Ver por
            {[['bono', 'Bono'], ['material', 'Material']].map(([k, n]) => (
              <Button
                key={k}
                size="small"
                variant={vistaPor === k ? 'contained' : 'outlined'}
                color="inherit"
                onClick={() => setVistaPor(k)}
                sx={{ textTransform: 'none', borderRadius: 16, py: 0.25, fontSize: 12, minWidth: 0 }}
              >
                {n}
              </Button>
            ))}
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7684' }}>
            Mostrando {promosFiltrados.length} de {promos.length}
            {vistaPor === 'material' ? ` bonos · ${filasMaterial.length} motos` : ''}
            {(filtroEst !== null || filtroEstado !== null) && (
              <Button size="small" sx={{ ml: 1, textTransform: 'none', fontSize: 12 }} onClick={() => { setFiltroEst(null); setFiltroEstado(null) }}>
                Quitar filtros
              </Button>
            )}
          </span>
        </div>
        {opcionesEst.length > 1 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {[{ clave: null, nombre: 'Todas las estrategias', n: promos.length }, ...opcionesEst].map((o) => {
              const activo = filtroEst === o.clave
              return (
                <Button
                  key={o.clave === null ? '__todas' : o.clave || '__sin'}
                  size="small"
                  variant={activo ? 'contained' : 'outlined'}
                  onClick={() => setFiltroEst(o.clave)}
                  sx={{ textTransform: 'none', borderRadius: 16, py: 0.25, fontSize: 12 }}
                >
                  {o.nombre}
                  <span style={{ marginLeft: 6, opacity: 0.75, fontVariantNumeric: 'tabular-nums' }}>{o.n}</span>
                </Button>
              )
            })}
          </div>
        )}
        <div style={{ marginTop: 10 }}>
          {/* key: al cambiar de vista la tabla se arma de nuevo (otras columnas, otro buscador). */}
          <TablaPrecios
            key={vistaPor}
            columns={vistaPor === 'material' ? columnsMaterial : columns}
            data={vistaPor === 'material' ? filasMaterial : promosFiltrados}
            isLoading={isLoading}
            pageSize={vistaPor === 'material' ? 25 : 15}
            // Clic en la fila = ver el bono (los botones de la fila no lo disparan).
            onRowClick={(r) => abrirBono(vistaPor === 'material' ? r.bono : r, 'ver')}
          />
        </div>
      </div>
    </div>
  )
}

export default PromosTab
